import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, ImagePlus, Loader2, Monitor, Tablet, Smartphone, RotateCcw, Check, Palette, X, Maximize2, Eye, History } from 'lucide-react';
import {
  designSite, confirmSiteDesignPlan, fetchSiteDesigns, fetchDesignProgress, fetchMySite, publishSite, rollbackSite, uploadPostImage,
  uploadDesignReference, fetchDesignReference, fetchSiteHistory, lookAtVersion, restoreVersion,
  type Site, type SiteDesignTurn, type SiteDesignLimits, type SiteHistoryEntry,
} from '@/services/siteApi';

// The AI Native Website Designer: the primary My Site surface once a member has a site.
// Left is one conversation: ask a question and it answers; ask for a small change and it
// makes it (a small edit); ask for a new look and it says its plan first, then redesigns
// only after a yes (Joe, Oct 29 2026: answer before doing, and tell small edits from
// major redesigns). Right is the preview, live, at desktop / tablet / phone widths.
// Nothing touches the live site until Publish. Joe, Oct 28 2026.
//
// A redesign runs in the background (minutes, on Opus), so the thread polls while a turn
// is running. A design reference (a mock, a site they like) is the one picture the
// designer actually looks at; attached photos stay paths, as everywhere else.

const INK = 'var(--hj-ink)';
const MUTED = 'var(--hj-ink-2)';
const panel: React.CSSProperties = { background: '#fff', border: '1px solid var(--hj-hairline-soft)', borderRadius: 16, padding: 20 };
const h2: React.CSSProperties = { margin: 0, fontFamily: 'var(--hj-font-display)', fontWeight: 800, fontSize: 20, letterSpacing: '-0.01em', color: INK };
const small: React.CSSProperties = { fontSize: 14, color: MUTED, margin: 0, lineHeight: 1.45 };
const body: React.CSSProperties = { fontSize: 15.5, lineHeight: 1.55, color: INK, margin: 0 };
const btnBase: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 44, padding: '10px 18px', borderRadius: 12, fontFamily: 'inherit', fontWeight: 600, fontSize: 15, cursor: 'pointer', boxSizing: 'border-box', textDecoration: 'none' };
const primary: React.CSSProperties = { ...btnBase, background: INK, color: '#fff', border: 0 };
const secondary: React.CSSProperties = { ...btnBase, background: '#fff', color: INK, border: '1px solid var(--hj-hairline)' };
const quiet: React.CSSProperties = { ...btnBase, minHeight: 40, padding: '8px 14px', fontSize: 14, fontWeight: 500, background: '#fff', color: INK, border: '1px solid var(--hj-hairline)', borderRadius: 10 };
const off = (s: React.CSSProperties, o: boolean): React.CSSProperties => (o ? { ...s, opacity: 0.45, cursor: 'not-allowed' } : s);
const LIVE_BG = '#e3f1d3', LIVE_FG = '#2f5d1a';

const bubbleYou: React.CSSProperties = { alignSelf: 'flex-end', maxWidth: '88%', background: 'var(--hj-surface-2)', color: INK, borderRadius: 14, borderBottomRightRadius: 4, padding: '10px 14px', fontSize: 15, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' };
type Tone = 'question' | 'done' | 'plan' | 'waiting' | 'failed';
const bubbleReply = (tone: Tone): React.CSSProperties => ({
  alignSelf: 'flex-start', maxWidth: '88%', borderRadius: 14, borderBottomLeftRadius: 4, padding: '10px 14px', fontSize: 15, lineHeight: 1.5, wordBreak: 'break-word', whiteSpace: 'pre-wrap',
  background: tone === 'question' || tone === 'plan' ? 'var(--hj-yellow-tint)' : tone === 'done' ? 'var(--hj-beige)' : 'var(--hj-surface-1)',
  color: tone === 'done' ? 'var(--hj-beige-ink)' : INK,
  border: tone === 'question' || tone === 'plan' ? '1px solid var(--hj-yellow)' : '1px solid transparent',
});
// What kind of reply this is, said plainly on the bubble, normal case.
const tag: React.CSSProperties = { display: 'inline-block', fontSize: 12.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: 'rgba(255,255,255,0.7)', color: INK, marginBottom: 6 };
const TAG: Record<SiteDesignTurn['kind'], string> = { answer: 'Answer', question: 'One question first', edit: 'Small edit', plan: 'Redesign plan', redesign: 'Redesign' };

// The designer writes a little markdown (**bold**, "# " headings, "- " bullets); show
// bold as bold and drop the other markers, instead of printing raw asterisks.
const Rich: React.FC<{ text: string | null | undefined }> = ({ text }) => {
  const clean = String(text || '').replace(/^#{1,6}\s+/gm, '').replace(/^\s*[-*]\s+/gm, '• ').replace(/__(.+?)__/g, '**$1**');
  return <>{clean.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 ? <strong key={i} style={{ fontWeight: 700 }}>{part}</strong> : part.replace(/\*\*/g, '')))}</>;
};

const EXAMPLES = [
  'Give the whole site a warmer, editorial look with a big headshot up top.',
  'What would make my home page stronger?',
  'Change my phone number on the Contact page to 555-0134.',
  'Rebuild the home page so my reel plays first and my credits sit under it.',
  'Make it feel more modern and minimal, lots of white space.',
];
const SHOW_TURNS = 12;

type Device = 'desktop' | 'tablet' | 'phone';
const DEVICES: { id: Device; label: string; w: number; h: number; Icon: typeof Monitor }[] = [
  { id: 'desktop', label: 'Desktop', w: 1280, h: 820, Icon: Monitor },
  { id: 'tablet', label: 'Tablet', w: 768, h: 1024, Icon: Tablet },
  { id: 'phone', label: 'Phone', w: 390, h: 844, Icon: Smartphone },
];

// A picture, scaled down to 1600 px on the long side and re-encoded as JPEG, as base64.
const shrink = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const k = Math.min(1, 1600 / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
    const g = c.getContext('2d');
    if (!g) { reject(new Error('Couldn’t read that picture.')); return; }
    g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height);
    g.drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    resolve(c.toDataURL('image/jpeg', 0.86).split(',')[1]);
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Couldn’t read that picture.')); };
  img.src = url;
});

// A reference thumbnail from the owner-only route.
const RefThumb: React.FC<{ siteId: string; id: string; size?: number }> = ({ siteId, id, size = 36 }) => {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null; let gone = false;
    fetchDesignReference(siteId, id).then((u) => { url = u; if (!gone) setSrc(u); }).catch(() => {});
    return () => { gone = true; if (url) URL.revokeObjectURL(url); };
  }, [siteId, id]);
  return src ? <img src={src} alt="Design reference" style={{ width: size, height: size, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--hj-hairline)' }} />
    : <span style={{ display: 'inline-block', width: size, height: size, borderRadius: 6, background: 'var(--hj-surface-2)' }} />;
};

// The designer's steps so far, in place of a bare spinner: done steps get a check,
// the current one spins, and a clock shows how long it's been at it.
const Steps: React.FC<{ steps: string[]; since: number; fallback: string }> = ({ steps, since, fallback }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(t); }, []);
  const secs = Math.max(0, Math.round((now - since) / 1000));
  const clock = secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${String(secs % 60).padStart(2, '0')}s`;
  const list = steps.length ? steps : [fallback];
  return (
    <div className="flex flex-col" style={{ gap: 6 }}>
      {list.map((label, i) => {
        const current = i === list.length - 1;
        return (
          <div key={i} className="flex" style={{ gap: 8, alignItems: 'flex-start', fontWeight: current ? 600 : 400 }}>
            {current
              ? <Loader2 className="w-4 h-4 animate-spin" style={{ flex: 'none', marginTop: 3 }} />
              : <Check size={16} aria-hidden="true" style={{ flex: 'none', marginTop: 3 }} />}
            <span>{label}{current && <span style={{ fontWeight: 400 }}> · {clock}</span>}</span>
          </div>
        );
      })}
    </div>
  );
};

const SiteDesigner: React.FC<{ site: Site; onSite: (s: Site) => void }> = ({ site, onSite }) => {
  const [prompt, setPrompt] = useState('');
  const [attachments, setAttachments] = useState<{ path: string; name: string }[]>([]);
  const [references, setReferences] = useState<{ id: string; preview: string }[]>([]);
  const [busy, setBusy] = useState<'idle' | 'uploading' | 'sending' | 'confirming' | 'publishing'>('idle');
  const [thread, setThread] = useState<SiteDesignTurn[]>([]);
  const [limits, setLimits] = useState<SiteDesignLimits | null>(null);
  const [published, setPublished] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0); // cache-busts the preview iframe after a change
  const [expanded, setExpanded] = useState(false); // full-screen preview, with the chat beside it
  const input = useRef<HTMLInputElement>(null);
  const refInput = useRef<HTMLInputElement>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const wasRunning = useRef(false);
  const [steps, setSteps] = useState<string[]>([]);
  const [sentAt, setSentAt] = useState(0);

  const canDesign = !!(site.liveVersionId || site.versions.length);
  const canSend = prompt.trim().length > 1 && busy === 'idle' && canDesign;

  const loadThread = () => fetchSiteDesigns(site.id).then((r) => { setThread(r.designs.slice().reverse()); setLimits(r.limits); return r.designs; }).catch(() => [] as SiteDesignTurn[]);
  useEffect(() => { loadThread(); }, [site.id]);
  useEffect(() => { if (thread.length) bottom.current?.scrollIntoView({ block: 'nearest' }); }, [thread.length]);

  // A redesign runs in the background: poll while one is running, and when it lands,
  // pick up the new draft so the preview and Publish show it.
  const runningTurn = thread.find((t) => t.kind === 'redesign' && t.status === 'running') || null;
  useEffect(() => {
    if (!runningTurn) {
      if (wasRunning.current) {
        wasRunning.current = false;
        fetchMySite().then((m) => { if (m.site) onSite(m.site); setReloadKey((k) => k + 1); }).catch(() => {});
      }
      return;
    }
    wasRunning.current = true;
    const t = window.setInterval(() => { loadThread(); }, 5000);
    return () => window.clearInterval(t);
  }, [runningTurn?.id]);

  // While a message is out, poll what the designer is doing.
  useEffect(() => {
    if (busy !== 'sending') { setSteps([]); return; }
    let live = true;
    const tick = () => fetchDesignProgress(site.id).then((p) => { if (live && p.steps.length) setSteps(p.steps); }).catch(() => {});
    const t = window.setInterval(tick, 1500); tick();
    return () => { live = false; window.clearInterval(t); };
  }, [busy, site.id]);

  const lastTurn = thread[thread.length - 1] || null;
  const answering = !!(lastTurn && lastTurn.kind === 'question');
  const planWaiting = !!(lastTurn && lastTurn.kind === 'plan' && lastTurn.status === 'proposed');
  const isLive = (versionId: string | null) => !!versionId && (versionId === site.liveVersionId || published.has(versionId));

  // The unpublished draft sitting on the preview, if any.
  const pending = site.previewVersionId && site.previewVersionId !== site.liveVersionId
    ? site.versions.find((v) => v.id === site.previewVersionId) || null : null;

  const attach = async (file: File) => {
    setBusy('uploading'); setErr(null);
    try { const r = await uploadPostImage(site.id, file); setAttachments([...attachments, { path: r.path, name: file.name }]); }
    catch (e) { setErr((e as Error).message); }
    setBusy('idle');
    if (input.current) input.current.value = '';
  };
  const attachReference = async (file: File) => {
    setBusy('uploading'); setErr(null);
    try {
      const data = await shrink(file);
      const r = await uploadDesignReference(site.id, data, 'image/jpeg');
      setReferences([...references, { id: r.id, preview: `data:image/jpeg;base64,${data}` }].slice(-3));
    } catch (e) { setErr((e as Error).message); }
    setBusy('idle');
    if (refInput.current) refInput.current.value = '';
  };
  const after = (r: { site?: Site; limits?: SiteDesignLimits; turn?: SiteDesignTurn }) => {
    if (r.site) onSite(r.site);
    if (r.limits) setLimits(r.limits);
    if (r.turn?.versionId) setReloadKey((k) => k + 1);
  };
  const send = async () => {
    setSentAt(Date.now()); setSteps([]);
    setBusy('sending'); setErr(null);
    try {
      const r = await designSite(site.id, prompt.trim(), attachments.map((a) => a.path), references.map((x) => x.id));
      after(r);
      setPrompt(''); setAttachments([]); setReferences([]);
    } catch (e) { setErr((e as Error).message); }
    await loadThread();
    setBusy('idle');
  };
  const confirm = async (planId: string) => {
    setBusy('confirming'); setErr(null);
    try { after(await confirmSiteDesignPlan(site.id, planId)); } catch (e) { setErr((e as Error).message); }
    await loadThread();
    setBusy('idle');
  };
  const publish = async (versionId: string) => {
    setBusy('publishing'); setErr(null);
    try { onSite((await publishSite(site.id, versionId)).site); setPublished((s) => new Set(s).add(versionId)); setReloadKey((k) => k + 1); }
    catch (e) { setErr((e as Error).message); }
    setBusy('idle');
  };
  const restore = async (versionId: string) => {
    setBusy('publishing'); setErr(null);
    try { onSite((await restoreVersion(site.id, versionId)).site); setReloadKey((k) => k + 1); }
    catch (e) { setErr((e as Error).message); }
    setBusy('idle');
  };
  const undo = async () => {
    setBusy('publishing'); setErr(null);
    try { onSite((await rollbackSite(site.id)).site); setReloadKey((k) => k + 1); }
    catch (e) { setErr((e as Error).message); }
    setBusy('idle');
  };

  const shown = thread.slice(-SHOW_TURNS);
  const latest = shown[shown.length - 1] || null;
  const redesignsLeft = limits ? Math.max(0, limits.redesigns.cap - limits.redesigns.used) : null;
  const rd = limits?.redesigns;
  const redesignCost = rd?.cost ?? 2;
  const redesignUnlimited = !!rd?.unlimited;
  const redesignCredits = rd?.credits ?? null;
  // Past the free daily count a redesign costs credits, so the button stays live as long as
  // they have a free redesign left, enough credits, or an unlimited account.
  const canAffordRedesign = redesignUnlimited || (redesignCredits !== null && redesignCredits >= redesignCost);
  const canRedesign = (redesignsLeft ?? 1) > 0 || canAffordRedesign;

  const replyFor = (t: SiteDesignTurn, isLatest: boolean) => {
    if (t.kind === 'redesign' && t.status === 'running') {
      return (
        <div style={bubbleReply('waiting')}>
          <span style={tag}>Redesign</span>
          <Steps steps={t.progress || []} since={new Date(t.createdAt).getTime()} fallback="Redesigning your site from the plan" />
          <div style={{ marginTop: 8 }}>This takes a few minutes. You can leave this page; it lands on your preview.</div>
        </div>
      );
    }
    if (t.failed) {
      return <div style={bubbleReply('failed')}><span style={tag}>{TAG[t.kind]}</span><br />{t.error || 'That one didn’t go through. Try it again, or say it another way.'}</div>;
    }
    if (t.kind === 'answer') return <div style={bubbleReply('waiting')}><span style={tag}>Answer</span><br /><Rich text={t.reply} /></div>;
    if (t.kind === 'question' || (t.question && !t.versionId)) {
      return <div style={bubbleReply('question')}><span style={tag}>One question first</span><br /><Rich text={t.question || t.reply} /></div>;
    }
    if (t.kind === 'plan') {
      return (
        <div style={bubbleReply('plan')}>
          <span style={tag}>Redesign plan</span><br />
          <Rich text={t.reply} />
          {t.references.length > 0 && <div className="flex flex-wrap" style={{ gap: 6, marginTop: 8 }}>{t.references.map((id) => <RefThumb key={id} siteId={site.id} id={id} />)}</div>}
          {t.status === 'proposed' && isLatest && (
            <div className="flex flex-wrap items-center" style={{ gap: 10, marginTop: 10 }}>
              <button type="button" disabled={busy !== 'idle' || !canRedesign || !!runningTurn} onClick={() => confirm(t.id)} style={off({ ...primary, minHeight: 40 }, busy !== 'idle' || !canRedesign || !!runningTurn)}>
                {busy === 'confirming' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Yes, redesign it'}
              </button>
              <span style={{ fontSize: 13.5 }}>{!canRedesign ? 'You’re out of free redesigns and credits. Small edits still work.' : (redesignsLeft === 0 && !redesignUnlimited ? `This redesign will use ${redesignCost} credits.` : 'Or tell me what to change in the plan.')}</span>
            </div>
          )}
          {t.status === 'accepted' && <div style={{ fontSize: 13, marginTop: 6 }}>You said yes.</div>}
          {t.status === 'superseded' && <div style={{ fontSize: 13, marginTop: 6 }}>Replaced by a newer plan.</div>}
        </div>
      );
    }
    // a finished small edit or redesign
    // View change: the preview URL serves whatever is on the preview, so the link only
    // shows while THIS turn's version is the one there. The version id doubles as a
    // cache-buster, so the new tab can't show the page from before the change.
    const onPreview = !!t.versionId && t.versionId === site.previewVersionId && !t.held && !!site.previewUrl;
    const changeUrl = onPreview ? `${site.previewUrl}${site.previewUrl!.includes('?') ? '&' : '?'}hjv=${t.versionId}` : null;
    return (
      <div style={bubbleReply('done')}>
        <span style={tag}>{TAG[t.kind]}</span><br />
        <span style={{ fontWeight: 700 }}>{isLive(t.versionId) ? 'Published. ' : t.held ? 'Done, and it’s getting a quick review first. ' : 'Done. '}</span>
        <Rich text={t.summary || t.reply} />
        {t.charged ? <span style={{ color: MUTED }}>{` (used ${t.charged} ${t.charged === 1 ? 'credit' : 'credits'})`}</span> : null}
        {isLatest && t.versionId && !isLive(t.versionId) && !t.held && (
          <div style={{ fontSize: 13, marginTop: 8 }}>It’s on the preview now. Look it over, then Publish on the right. Not right? Say what to fix.</div>
        )}
        {changeUrl && (
          <div style={{ marginTop: 10 }}>
            <a href={changeUrl} target="_blank" rel="noreferrer" style={quiet}>View change <ExternalLink size={14} aria-hidden="true" /></a>
          </div>
        )}
      </div>
    );
  };

  // The conversation. Rendered once: on the page, or (expanded) beside the full-screen
  // preview, so its refs and draft stay in one place.
  const chat = (inOverlay: boolean) => (
      <section id="design" className="flex flex-col" style={inOverlay
        // maxHeight pins the panel to the overlay's height, so the thread scrolls inside
        // it. Without it the panel grew past the screen and its end was unreachable.
        ? { ...panel, gap: 12, padding: 16, flex: '0 1 380px', minWidth: 0, minHeight: 0, maxHeight: '100%', boxSizing: 'border-box', overflowY: 'auto' }
        : { ...panel, gap: 14, flex: '1 1 360px', minWidth: 0, alignSelf: 'stretch' }}>
        {!inOverlay && <div className="flex flex-col" style={{ gap: 6 }}>
          <h2 style={h2}>Design my site</h2>
          <p style={body}>Ask me anything about your site, ask for a change, or describe a new look. Small changes happen right away. For a redesign I tell you my plan first and start when you say yes. Your live site doesn’t change until you press Publish.</p>
          {limits && (
            <p style={{ ...small, fontSize: 13, color: !canRedesign ? INK : MUTED, fontWeight: !canRedesign ? 600 : 400 }}>
              {redesignUnlimited
                ? 'Redesigns and small edits are unlimited on your account.'
                : (redesignsLeft && redesignsLeft > 0
                    ? `${redesignsLeft} of ${rd?.free ?? limits.redesigns.cap} free redesigns left today, then ${redesignCost} credits each. Small edits are always free.`
                    : (canAffordRedesign
                        ? `Today’s free redesigns are used. Each redesign now uses ${redesignCost} credits${redesignCredits !== null ? ` (you have ${redesignCredits})` : ''}. Small edits are always free.`
                        : 'You’re out of free redesigns and credits for now. Small edits still work, and they’re always free.'))}
            </p>
          )}
        </div>}
        {inOverlay && <h2 style={{ ...h2, fontSize: 18 }}>Design my site</h2>}
        {!canDesign && <p style={small}>Add your site first, up top, then you can design it from here.</p>}

        {shown.length > 0 && (
          <div className="flex flex-col" style={{ gap: 10, padding: '4px 0', maxHeight: inOverlay ? 'none' : 520, flex: inOverlay ? '1 1 auto' : undefined, minHeight: 0, overflowY: 'auto' }}>
            {thread.length > SHOW_TURNS && <p style={{ ...small, fontSize: 12.5, textAlign: 'center' }}>Earlier changes are in your versions list.</p>}
            {shown.map((t) => (
              <React.Fragment key={t.id}>
                <div style={bubbleYou}>
                  {t.prompt}
                  {(t.attachments.length > 0 || t.references.length > 0) && (
                    <div className="flex flex-wrap" style={{ gap: 6, marginTop: 8 }}>
                      {t.attachments.map((p) => <img key={p} src={p} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }} />)}
                      {t.kind !== 'plan' && t.references.map((id) => <RefThumb key={id} siteId={site.id} id={id} />)}
                    </div>
                  )}
                </div>
                {replyFor(t, t === latest)}
              </React.Fragment>
            ))}
            {busy === 'sending' && (
              <div style={bubbleReply('waiting')}><Steps steps={steps} since={sentAt || Date.now()} fallback="Reading your message" /></div>
            )}
            <div ref={bottom} />
          </div>
        )}

        <textarea aria-label={answering ? 'Your answer' : 'Message the designer'} value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
          placeholder={answering ? 'Reply' : planWaiting ? 'Say yes, or what to change in the plan' : EXAMPLES[Math.floor(Date.now() / 60000) % EXAMPLES.length]}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); if (canSend) send(); } }}
          style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${answering || planWaiting ? 'var(--hj-yellow)' : 'var(--hj-hairline)'}`, borderRadius: 10, padding: '10px 12px', font: '15px var(--hj-font-ui)', color: INK, background: '#fff', resize: 'vertical' }} />
        {(attachments.length > 0 || references.length > 0) && (
          <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
            {attachments.map((a) => <span key={a.path} className="flex items-center" style={{ gap: 8, fontSize: 13, color: INK, background: 'var(--hj-surface-1)', borderRadius: 8, padding: '4px 8px' }}><img src={a.path} alt="" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 6 }} />{a.name}</span>)}
            {references.map((r) => (
              <span key={r.id} className="flex items-center" style={{ gap: 8, fontSize: 13, color: INK, background: 'var(--hj-yellow-tint)', borderRadius: 8, padding: '4px 8px' }}>
                <img src={r.preview} alt="" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 6 }} />Design reference
                <button type="button" aria-label="Remove this reference" onClick={() => setReferences(references.filter((x) => x.id !== r.id))} style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 2, display: 'inline-flex' }}><X size={14} /></button>
              </span>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
          <button type="button" disabled={!canSend} onClick={send} style={off(primary, !canSend)}>{busy === 'sending' ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending</> : answering ? 'Reply' : 'Send'}</button>
          <input ref={input} type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) attach(f); }} />
          <button type="button" disabled={busy !== 'idle'} onClick={() => input.current?.click()} style={off(quiet, busy !== 'idle')}>{busy === 'uploading' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ImagePlus size={16} aria-hidden="true" /> Attach a photo</>}</button>
          <input ref={refInput} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) attachReference(f); }} />
          <button type="button" disabled={busy !== 'idle' || references.length >= 3} onClick={() => refInput.current?.click()} style={off(quiet, busy !== 'idle' || references.length >= 3)}><Palette size={16} aria-hidden="true" /> Attach a design reference</button>
        </div>
        {!inOverlay && <p style={{ ...small, fontSize: 12.5 }}>A design reference is a mock or a site you like, and the designer looks at it to match the look. Photos of you go in with Attach a photo, and those are placed on your site, never shown to the AI. We never make up names, numbers or credits; if something’s missing we ask.</p>}
        {err && <p style={{ ...small, color: 'var(--hj-pink-ink)' }}>{err}</p>}
      </section>
  );

  return (
    <div className="flex flex-wrap items-start" style={{ gap: 20 }}>
      {/* Left: one conversation (it moves beside the preview when expanded) */}
      {!expanded && chat(false)}

      {/* Right: live preview at three widths, with Publish / Undo */}
      <PreviewPane
        expanded={expanded}
        onExpand={setExpanded}
        chat={expanded ? chat(true) : null}
        site={site}
        reloadKey={reloadKey}
        pendingReady={!!(pending && pending.status === 'ready')}
        pendingVersionId={pending?.status === 'ready' ? pending.id : null}
        busy={busy === 'publishing'}
        onPublish={publish}
        onUndo={undo}
        onRestore={restore}
        historyKey={`${site.previewVersionId}-${site.liveVersionId}-${reloadKey}-${thread.length}`}
      />
    </div>
  );
};

// The CSS size of an element, kept current as it resizes.
const useSize = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size] as const;
};

const DeviceToggle: React.FC<{ device: Device; onDevice: (d: Device) => void }> = ({ device, onDevice }) => (
  <div className="flex items-center" role="group" aria-label="Preview width" style={{ gap: 6, background: 'var(--hj-surface-1)', padding: 4, borderRadius: 12 }}>
    {DEVICES.map(({ id, label, Icon }) => {
      const on = id === device;
      return (
        <button key={id} type="button" aria-pressed={on} onClick={() => onDevice(id)}
          className="inline-flex items-center" style={{
            gap: 6, padding: '8px 12px', minHeight: 40, borderRadius: 9, border: 0, cursor: 'pointer',
            fontFamily: 'inherit', fontWeight: on ? 700 : 500, fontSize: 14,
            background: on ? '#fff' : 'transparent', color: INK,
            boxShadow: on ? '0 1px 2px rgba(40,36,30,0.14)' : 'none',
          }}>
          <Icon size={16} aria-hidden="true" /> {label}
        </button>
      );
    })}
  </div>
);

// The site rendered at the device's real width, scaled down to fit availW. `fill` makes
// it as tall as maxH (the full-screen view); otherwise it keeps the device's shape.
const Frame: React.FC<{ url: string; frameKey: string; device: Device; availW: number; maxH: number; fill?: boolean }> = ({ url, frameKey, device, availW, maxH, fill }) => {
  const d = DEVICES.find((x) => x.id === device)!;
  const scale = availW > 0 ? Math.min(1, availW / d.w) : 1;
  const frameH = Math.max(240, fill ? maxH : Math.min(maxH, Math.round(d.h * scale)));
  return (
    <div style={{ width: Math.round(d.w * scale), maxWidth: '100%', height: frameH, margin: '0 auto', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--hj-hairline)', background: '#fff' }}>
      <iframe key={frameKey} src={url} title="Site preview" loading="lazy" sandbox="allow-scripts allow-same-origin"
        style={{ width: d.w, height: Math.round(frameH / scale), border: 0, transform: `scale(${scale})`, transformOrigin: '0 0', background: '#fff' }} />
    </div>
  );
};

// The preview filling the window, at real size when it fits. Escape or Close returns.
const ExpandedPreview: React.FC<{
  url: string; reloadKey: number; device: Device; onDevice: (d: Device) => void;
  note: React.ReactNode; publishBtn: React.ReactNode; chat: React.ReactNode; onClose: () => void;
}> = ({ url, reloadKey, device, onDevice, note, publishBtn, chat, onClose }) => {
  const [area, size] = useSize();
  const closeBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtn.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prevOverflow; window.removeEventListener('keydown', onKey); };
  }, []);
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Site preview" className="flex flex-col"
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--hj-surface-1)', padding: 16, gap: 12, boxSizing: 'border-box' }}>
      <div className="flex flex-wrap items-center justify-between" style={{ gap: 12 }}>
        <div className="flex flex-wrap items-center" style={{ gap: 12, minWidth: 0 }}>
          <h2 style={h2}>Preview</h2>
          {note}
        </div>
        <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
          <DeviceToggle device={device} onDevice={onDevice} />
          {publishBtn}
          <a href={url} target="_blank" rel="noreferrer" style={secondary}>Open in a new tab <ExternalLink size={16} aria-hidden="true" /></a>
          <button ref={closeBtn} type="button" onClick={onClose} style={secondary}><X size={16} aria-hidden="true" /> Close</button>
        </div>
      </div>
      <div className="flex" style={{ flex: 1, minHeight: 0, gap: 12, flexWrap: 'wrap', overflowY: 'auto' }}>
        {chat}
        <div ref={area} style={{ flex: '1 1 480px', minHeight: 320, minWidth: 0 }}>
          {size.h > 0 && <Frame url={url} frameKey={`full-${device}-${reloadKey}`} device={device} availW={size.w} maxH={size.h} fill />}
        </div>
      </div>
    </div>,
    document.body,
  );
};

const PreviewPane: React.FC<{
  site: Site; reloadKey: number; pendingReady: boolean; pendingVersionId: string | null;
  busy: boolean; onPublish: (versionId: string) => void; onUndo: () => void; onRestore: (versionId: string) => Promise<void>; historyKey: string;
  expanded: boolean; onExpand: (v: boolean) => void; chat: React.ReactNode;
}> = ({ site, reloadKey, pendingReady, pendingVersionId, busy, onPublish, onUndo, onRestore, historyKey, expanded, onExpand, chat }) => {
  const [device, setDevice] = useState<Device>('desktop');
  const [box, size] = useSize();
  const [history, setHistory] = useState<SiteHistoryEntry[] | null>(null);
  const [showAll, setShowAll] = useState(false);
  // Looking at an old version: the frame shows it from the private look address.
  const [look, setLook] = useState<{ id: string; url: string; when: string } | null>(null);
  const [lookBusy, setLookBusy] = useState<string | null>(null);
  const [histErr, setHistErr] = useState<string | null>(null);
  useEffect(() => { fetchSiteHistory(site.id).then((r) => setHistory(r.versions)).catch(() => setHistory([])); }, [site.id, historyKey]);

  const draftUrl = site.previewUrl ? `${site.previewUrl}${site.previewUrl.includes('?') ? '&' : '?'}hjv=${reloadKey}` : null;
  const previewUrl = look ? look.url : draftUrl;
  const prev = site.versions.find((v) => v.id === site.previousVersionId) || null;
  const when = (s: string) => new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const openLook = async (h: SiteHistoryEntry) => {
    setLookBusy(h.id); setHistErr(null);
    try { const r = await lookAtVersion(site.id, h.id); setLook({ id: h.id, url: r.url, when: when(h.at) }); box.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }
    catch (e) { setHistErr((e as Error).message); }
    setLookBusy(null);
  };
  const bringBack = async (id: string) => { await onRestore(id); setLook(null); };
  const note = look
    ? (
      <div className="flex flex-wrap items-center" style={{ gap: 10, background: 'var(--hj-yellow-tint)', borderRadius: 10, padding: '8px 12px' }}>
        <span style={{ fontSize: 14, color: INK, fontWeight: 600 }}>You’re looking at the version from {look.when}. Nothing has changed.</span>
        <button type="button" disabled={busy} onClick={() => bringBack(look.id)} style={off({ ...quiet, minHeight: 36 }, busy)}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Make this my draft'}</button>
        <button type="button" onClick={() => setLook(null)} style={{ ...quiet, minHeight: 36 }}>Back to my draft</button>
      </div>
    )
    : pendingReady
    ? <p style={{ ...small, color: INK, fontWeight: 600 }}>This is a draft. It isn’t live until you publish it.</p>
    : <p style={small}>{site.liveVersionId ? 'This is your live site. New changes show here as a draft before you publish.' : 'Your site shows here once it’s on the preview.'}</p>;
  const publishBtn = !look && pendingReady && pendingVersionId && (
    <button type="button" disabled={busy} onClick={() => onPublish(pendingVersionId)} style={off({ ...primary, minHeight: 48 }, busy)}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check size={16} aria-hidden="true" /> Publish to my live site</>}
    </button>
  );

  return (
    <section className="flex flex-col" style={{ ...panel, gap: 14, flex: '2 1 560px', minWidth: 0, alignSelf: 'stretch', position: 'sticky', top: 16 }}>
      <div className="flex flex-wrap items-center justify-between" style={{ gap: 12 }}>
        <h2 style={h2}>Preview</h2>
        <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
          <DeviceToggle device={device} onDevice={setDevice} />
          {previewUrl && <button type="button" onClick={() => onExpand(true)} style={quiet}><Maximize2 size={16} aria-hidden="true" /> Expand</button>}
        </div>
      </div>

      {note}

      <div ref={box} style={{ width: '100%' }}>
        {previewUrl ? (
          <Frame url={previewUrl} frameKey={`${device}-${reloadKey}-${look ? look.id : 'draft'}`} device={device} availW={size.w} maxH={760} />
        ) : (
          <div className="flex items-center justify-center text-center" style={{ height: 320, borderRadius: 12, border: '1px dashed var(--hj-hairline)', color: 'var(--hj-ink-3)', fontSize: 14, padding: 16 }}>
            Your site shows here once it’s on the preview.
          </div>
        )}
      </div>

      {expanded && previewUrl && (
        <ExpandedPreview url={previewUrl} reloadKey={reloadKey} device={device} onDevice={setDevice} note={note} publishBtn={publishBtn} chat={chat} onClose={() => onExpand(false)} />
      )}

      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        {publishBtn}
        {previewUrl && <a href={previewUrl} target="_blank" rel="noreferrer" style={secondary}>Open in a new tab <ExternalLink size={16} aria-hidden="true" /></a>}
        {prev && (
          <button type="button" disabled={busy} onClick={onUndo} style={off(quiet, busy)}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw size={14} aria-hidden="true" /> Undo last publish</>}
          </button>
        )}
      </div>

      {history && history.length > 0 && (
        <div className="flex flex-col" style={{ gap: 10, borderTop: '1px solid var(--hj-hairline-soft)', paddingTop: 14 }}>
          <h3 className="flex items-center" style={{ margin: 0, gap: 8, fontSize: 17, fontWeight: 700, color: INK }}><History size={18} aria-hidden="true" /> History</h3>
          <p style={{ ...small, fontSize: 13 }}>Every change, newest first. Look at any of them without changing anything, or bring one back as your draft. Your live site only changes when you publish.</p>
          {histErr && <p style={{ ...small, color: 'var(--hj-pink-ink)' }}>{histErr}</p>}
          <ol className="flex flex-col" style={{ gap: 0, margin: 0, padding: 0, listStyle: 'none' }}>
            {(showAll ? history : history.slice(0, 6)).map((h) => (
              <li key={h.id} className="flex flex-wrap items-center justify-between" style={{ gap: 10, padding: '10px 0', borderTop: '1px solid var(--hj-hairline-soft)', background: look?.id === h.id ? 'var(--hj-yellow-tint)' : undefined }}>
                <div className="flex flex-col" style={{ gap: 2, minWidth: 0, flex: '1 1 260px' }}>
                  <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
                    <span style={{ fontSize: 13, color: MUTED }}>{when(h.at)}</span>
                    {h.live && <span style={{ background: LIVE_BG, color: LIVE_FG, fontWeight: 600, fontSize: 12, padding: '2px 8px', borderRadius: 999 }}>Live</span>}
                    {h.draft && !h.live && <span style={{ background: 'var(--hj-yellow-tint)', color: INK, fontWeight: 600, fontSize: 12, padding: '2px 8px', borderRadius: 999 }}>Your draft</span>}
                    {h.status === 'review' && <span style={{ background: 'var(--hj-surface-1)', color: INK, fontWeight: 600, fontSize: 12, padding: '2px 8px', borderRadius: 999 }}>In review</span>}
                  </div>
                  <span style={{ fontSize: 14.5, color: INK, overflowWrap: 'anywhere' }}>{h.what}</span>
                  {h.asked && h.asked !== h.what && <span style={{ fontSize: 13, color: MUTED, overflowWrap: 'anywhere' }}>You asked: “{h.asked}”</span>}
                </div>
                {h.status === 'ready' && (
                  <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
                    {!h.draft && (
                      <button type="button" disabled={lookBusy === h.id} onClick={() => openLook(h)} style={off({ ...quiet, minHeight: 36 }, lookBusy === h.id)}>
                        {lookBusy === h.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Eye size={14} aria-hidden="true" /> Look at it</>}
                      </button>
                    )}
                    {!h.draft && (
                      <button type="button" disabled={busy} onClick={() => bringBack(h.id)} style={off({ ...quiet, minHeight: 36 }, busy)}>
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw size={14} aria-hidden="true" /> Make this my draft</>}
                      </button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ol>
          {history.length > 6 && (
            <button type="button" onClick={() => setShowAll((v) => !v)} style={{ ...quiet, alignSelf: 'flex-start' }}>{showAll ? 'Show fewer' : `Show all ${history.length}`}</button>
          )}
          <p style={{ ...small, fontSize: 12.5 }}>We keep your last 30 versions. Downloads are in Settings.</p>
        </div>
      )}
    </section>
  );
};

export default SiteDesigner;
